import { FilterInputDto, RecordStatus } from "./service-proxies";
import {
  IResolvedApiResource,
  GetFilteredListFn,
  GetOneFn,
  DeleteOneFn,
  CreateOneFn,
  UpdateOneFn,
  IPagedResultDto,
  DeleteManyFn,
  GetManyFn,
  GetManyReferenceFn,
  UpdateManyFn,
  UpdateManyDto,
  ChangeStatusFn,
} from "./types";
import { AxiosPromise } from "axios";
import { Record, Identifier } from "react-admin";

export interface IServiceProxyFactoryResult {
  getFilteredList: GetFilteredListFn;
  getOneById: GetOneFn;
  deleteOne: DeleteOneFn;
  createOne: CreateOneFn;
  updateOne: UpdateOneFn;
  deleteMany: DeleteManyFn;
  getMany: GetManyFn;
  getManyReference: GetManyReferenceFn;
  updateMany: UpdateManyFn;
  changeStatus?: ChangeStatusFn;
}

export const serviceProxyFactory = (
  resolvedApiResource: IResolvedApiResource
): IServiceProxyFactoryResult => ({
  getFilteredList: <RecordType extends Record = Record>(
    filterInputDto?: FilterInputDto,
    options?: any
  ): AxiosPromise<IPagedResultDto<RecordType>> => {
    const operationName = resolvedApiResource.isMockResource
      ? "getFilteredList"
      : `apiApp${resolvedApiResource.apiRouteName}GetFilteredListPost`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(filterInputDto, options);
  },

  getOneById: <RecordType extends Record = Record>(
    id: string,
    options?: any
  ): AxiosPromise<RecordType> => {
    const operationName = resolvedApiResource.isMockResource
      ? "getOneById"
      : `apiApp${resolvedApiResource.apiRouteName}IdGet`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(id, options);
  },

  deleteOne: (id: string, options?: any): AxiosPromise<void> => {
    const operationName = resolvedApiResource.isMockResource
      ? "deleteOne"
      : `apiApp${resolvedApiResource.apiRouteName}IdDelete`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(id, options);
  },

  updateOne: <RecordType extends Record = Record>(
    id: string,
    dto?: any,
    options?: any
  ): AxiosPromise<RecordType> => {
    const operationName = resolvedApiResource.isMockResource
      ? "updateOne"
      : `apiApp${resolvedApiResource.apiRouteName}IdPut`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(id, dto, options);
  },

  createOne: <RecordType extends Record = Record>(
    dto?: any,
    options?: any
  ): AxiosPromise<RecordType> => {
    const operationName = resolvedApiResource.isMockResource
      ? "createOne"
      : `apiApp${resolvedApiResource.apiRouteName}Post`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(dto, options);
  },

  deleteMany: (ids?: string[], options?: any) => {
    const operationName = resolvedApiResource.isMockResource
      ? "deleteMany"
      : `apiApp${resolvedApiResource.apiRouteName}DeleteManyDelete`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(ids, options);
  },

  getMany: (ids?: string[], options?: any) => {
    const operationName = resolvedApiResource.isMockResource
      ? "getMany"
      : `apiApp${resolvedApiResource.apiRouteName}GetManyGet`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(ids, options);
  },

  getManyReference: (filterInputDto?: FilterInputDto, options?: any) => {
    const operationName = resolvedApiResource.isMockResource
      ? "getFilteredList"
      : `apiApp${resolvedApiResource.apiRouteName}GetManyGet`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(filterInputDto, options);
  },

  updateMany: (dto?: UpdateManyDto, options?: any) => {
    const operationName = resolvedApiResource.isMockResource
      ? "updateMany"
      : `apiApp${resolvedApiResource.apiRouteName}UpdateManyPut`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(dto, options);
  },

  changeStatus: (id: Identifier, status: RecordStatus, options?: any) => {
    const operationName = resolvedApiResource.isMockResource
      ? "changeStatus"
      : `apiApp${resolvedApiResource.apiRouteName}ChangeStatusPost`;
    if (!resolvedApiResource.api.hasOwnProperty(operationName)) {
      throw new Error(
        `The resolved API proxy for resource '${resolvedApiResource.apiRouteName}' doesn't have the required operation '${operationName}'`
      );
    }

    const fn = resolvedApiResource.api[operationName];
    return fn(id, status, options);
  },
});
